/**
 * useLandingARKflowLandingLessonDemoLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait LandingARKflowLandingLessonDemo
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, LandingARKflowLandingLesson, LandingARKflowLandingLessonDemoEventPayloadMap } from '@app/shared';
import { useUIEvents } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';
import { useTranslate } from '@almadar/ui/hooks';

// ============================================================================
// State Machine
// ============================================================================

export type LandingARKflowLandingLessonDemoState =
  | 'reading'
  | 'prompting';

export type LandingARKflowLandingLessonDemoEvent =
  | 'INIT'
  | 'LESSON_ASK'
  | 'LESSON_NOTE'
  | 'CLOSE';

type LandingARKflowLandingLessonDemoSlot = 'main' | 'modal';
interface LandingARKflowLandingLessonDemoInternalState {
  machineState: LandingARKflowLandingLessonDemoState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: LandingARKflowLandingLessonDemoEvent | null;
  slotWriter: Partial<Record<LandingARKflowLandingLessonDemoSlot, LandingARKflowLandingLessonDemoEvent | null>>;
  loading: boolean;
  pendingEvent: LandingARKflowLandingLessonDemoEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<LandingARKflowLandingLesson>;
}

type LandingARKflowLandingLessonDemoAction =
  | { type: 'EVENT_SUCCESS'; event: LandingARKflowLandingLessonDemoEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: LandingARKflowLandingLessonDemoSlot[]; renderClear?: LandingARKflowLandingLessonDemoSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: LandingARKflowLandingLessonDemoEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof LandingARKflowLandingLesson & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function landingARKflowLandingLessonDemoReducer(
  state: LandingARKflowLandingLessonDemoInternalState,
  action: LandingARKflowLandingLessonDemoAction
): LandingARKflowLandingLessonDemoInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as LandingARKflowLandingLessonDemoState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<LandingARKflowLandingLesson>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<LandingARKflowLandingLesson>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<LandingARKflowLandingLesson>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: LandingARKflowLandingLessonDemoInternalState = {
  machineState: 'reading',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

export interface LandingARKflowLandingLessonDemoConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  lessonView: { children: Array<{ align: string; children: Array<{ children: Array<{ color: string; content: string[]; type: string; variant: string } | { content: string[]; type: string; variant: string }>; direction: string; gap: string; type: string } | { color: string; content: string[]; type: string; variant: string }>; direction: string; gap: string; justify: string; type: string; wrap: boolean } | { askEvent: string; askLabel: string[]; lesson: string[]; noteEvent: string; noteLabel: string[]; type: string }>; direction: string; gap: string; type: string };
  signIn: string;
  signInPrompt: { children: Array<{ children: Array<{ action: string; label: string[]; type: string; variant: string } | { href: string; icon: string; label: string[]; type: string; variant: string }>; direction: string; gap: string; justify: string; type: string } | { children: { content: string; type: string; variant: string }[]; type: string } | { color: string; content: string[]; type: string; variant: string } | { content: string[]; type: string; variant: string }>; direction: string; gap: string; type: string };
}

const DEFAULT_LANDING_AR_KFLOW_LANDING_LESSON_DEMO_CONFIG = {
  lessonView: { children: [{ align: "center", children: [{ children: [{ color: "muted", content: ["i18n/t", "kflow-landing:lesson.sampleLevel"], type: "typography", variant: "caption" }, { content: ["i18n/t", "kflow-landing:lesson.sampleTitle"], type: "typography", variant: "h3" }], direction: "vertical", gap: "xs", type: "stack" }, { color: "muted", content: ["i18n/t", "kflow-landing:lesson.sampleBadge"], type: "typography", variant: "caption" }], direction: "horizontal", gap: "sm", justify: "between", type: "stack", wrap: true }, { askEvent: "LESSON_ASK", askLabel: ["i18n/t", "kflow-landing:lesson.askLabel"], lesson: ["i18n/t", "kflow-landing:lesson.sample"], noteEvent: "LESSON_NOTE", noteLabel: ["i18n/t", "kflow-landing:lesson.noteLabel"], type: "segment-renderer" }], direction: "vertical", gap: "md", type: "stack" },
  signIn: "https://kflow-app.web.app/login",
  signInPrompt: { children: [{ content: ["i18n/t", "kflow-landing:lesson.promptTitle"], type: "typography", variant: "h4" }, { children: [{ content: "@entity.prompt", type: "typography", variant: "body" }], type: "card" }, { color: "muted", content: ["i18n/t", "kflow-landing:lesson.promptBody"], type: "typography", variant: "body" }, { children: [{ action: "CLOSE", label: ["i18n/t", "kflow-landing:lesson.promptLater"], type: "button", variant: "ghost" }, { href: "@config.signIn", icon: "arrow-right", label: ["i18n/t", "kflow-landing:lesson.promptSignIn"], type: "button", variant: "primary" }], direction: "horizontal", gap: "sm", justify: "end", type: "stack" }], direction: "vertical", gap: "md", type: "stack" },
} as LandingARKflowLandingLessonDemoConfig;

export interface LandingARKflowLandingLessonDemoLogicReturn {
  state: LandingARKflowLandingLessonDemoState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: LandingARKflowLandingLessonDemoEvent | null;
  slotWriter: Partial<Record<LandingARKflowLandingLessonDemoSlot, LandingARKflowLandingLessonDemoEvent | null>>;
  loading: boolean;
  pendingEvent: LandingARKflowLandingLessonDemoEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<LandingARKflowLandingLesson>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof LandingARKflowLandingLessonDemoEventPayloadMap]: (payload?: LandingARKflowLandingLessonDemoEventPayloadMap[K]) => Promise<EventResponse> };
  config: LandingARKflowLandingLessonDemoConfig;
}

export function useLandingARKflowLandingLessonDemoLogic(config?: Partial<LandingARKflowLandingLessonDemoConfig>): LandingARKflowLandingLessonDemoLogicReturn {
  const mergedConfig: LandingARKflowLandingLessonDemoConfig = { ...DEFAULT_LANDING_AR_KFLOW_LANDING_LESSON_DEMO_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/landing-ar-kflow-landing-lesson-demo', orbitalName: 'LandingAR', traitName: 'LandingARKflowLandingLessonDemo' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(landingARKflowLandingLessonDemoReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  const { t: __t, locale: __locale } = useTranslate();
  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const payloadObj = payload;
    const entity: EntityRow[] = liveData['LandingARKflowLandingLesson'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};


    if (fromState === 'reading' && event === 'INIT') {
      // reading → reading (on INIT)
      _newState = 'reading';
    } else if (fromState === 'reading' && event === 'LESSON_ASK') {
      // reading → prompting (on LESSON_ASK)
        _fields['prompt'] = payloadObj?.selectedText;
        fields = { ...(fields ?? {}), 'prompt': _fields['prompt'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'prompting';
    } else if (fromState === 'reading' && event === 'LESSON_NOTE') {
      // reading → prompting (on LESSON_NOTE)
        _fields['prompt'] = payloadObj?.selectedText;
        fields = { ...(fields ?? {}), 'prompt': _fields['prompt'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'prompting';
    } else if (fromState === 'prompting' && event === 'INIT') {
      // prompting → reading (on INIT)
      _newState = 'reading';
    } else if (fromState === 'prompting' && event === 'CLOSE') {
      // prompting → reading (on CLOSE)
      _newState = 'reading';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: LandingARKflowLandingLessonDemoSlot[]; clear: LandingARKflowLandingLessonDemoSlot[] } => {
    switch (fromState) {
      case 'prompting':
        switch (event) {
          case 'CLOSE': return { write: ['main'] as LandingARKflowLandingLessonDemoSlot[], clear: ['modal'] as LandingARKflowLandingLessonDemoSlot[] };
          case 'INIT': return { write: ['main'] as LandingARKflowLandingLessonDemoSlot[], clear: ['modal'] as LandingARKflowLandingLessonDemoSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'reading':
        switch (event) {
          case 'INIT': return { write: ['main'] as LandingARKflowLandingLessonDemoSlot[], clear: [] as LandingARKflowLandingLessonDemoSlot[] };
          case 'LESSON_ASK': return { write: ['modal'] as LandingARKflowLandingLessonDemoSlot[], clear: [] as LandingARKflowLandingLessonDemoSlot[] };
          case 'LESSON_NOTE': return { write: ['modal'] as LandingARKflowLandingLessonDemoSlot[], clear: [] as LandingARKflowLandingLessonDemoSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'LandingARKflowLandingLesson': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as LandingARKflowLandingLessonDemoEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    void sendEvent; void commandPump; // no server-touching effect on this trait; shared-store commit above is the only sync needed
    return { success: true, transitioned: true, states: { 'LandingARKflowLandingLessonDemo': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof LandingARKflowLandingLessonDemoEventPayloadMap]: (payload?: LandingARKflowLandingLessonDemoEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'LESSON_ASK': (payload?: LandingARKflowLandingLessonDemoEventPayloadMap['LESSON_ASK']) => dispatch('LESSON_ASK', payload),
    'LESSON_NOTE': (payload?: LandingARKflowLandingLessonDemoEventPayloadMap['LESSON_NOTE']) => dispatch('LESSON_NOTE', payload),
    'CLOSE': () => dispatch('CLOSE'),
  };

  useUIEvents(enqueueEvent, 'LandingAR.LandingARKflowLandingLessonDemo', ['INIT', 'LESSON_ASK', 'LESSON_NOTE', 'CLOSE'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('LandingARKflowLandingLessonDemo', (): TraitStateSnapshot => ({
      traitName: 'LandingARKflowLandingLessonDemo',
      currentState: internalStateRef.current.machineState,
      states: ['reading', 'prompting'],
      events: ['INIT', 'LESSON_ASK', 'LESSON_NOTE', 'CLOSE'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: [],
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
